/**
 * scheduleRoster.ts
 * Frontend service for the per-week WORK-ORDER / QUOTE staffing roster —
 * the primary allocation surface (PR A/B, D3 of
 * qa/staffing-allocation-2026-07-13/PLAN.md).
 *
 * Wraps PR A's endpoints (all through apiClient — MSAL auth + error capture):
 *   GET    /api/schedule/roster?source=&sourceKey=&scenarioKey=
 *   PUT    /api/schedule/roster  { source, sourceKey, scenarioKey, userKey, weekStart, percent }
 *   DELETE /api/schedule/roster  { source, sourceKey, scenarioKey, userKey, weekStart }
 *
 * Guardrails (D4): the PUT NEVER 4xx's on over-allocation — Σ% > 100 rides the
 * 200 body as `weekOverloaded`. The one hard stop is the day-off block (409
 * ABSENCE_DAY_CONFLICT), re-thrown as the shared AbsenceDayConflictError so the
 * UI shows the server message verbatim (Q3B — no bypass). A Submitted plan
 * 409s scenario_locked → ScenarioLockedError (remedy copy).
 */

import apiClient from './apiClient';
import {
  toAbsenceDayConflictError,
  toScenarioLockedError,
} from './scheduleAssignments';

/** The plan source a roster hangs off — the scenario's WO or Quote (D3). */
export type RosterSource = 'WorkOrder' | 'Quote';

/** One person on a source-week roster (mirrors backend RosterEntry). */
export interface RosterEntry {
  userKey: string;
  displayName: string;
  /** AppUser.Email — resolves MS Graph photos for roster-only members (2026-07-28). */
  email?: string | null;
  /** Allocation % (explicit row, or 100 fallback when present without a row). */
  percent: number;
  /** True when the person has active presence on ≥1 of the source's tasks that week. */
  present: boolean;
  /** True when an explicit EmployeeSourceAllocation row backs the percent. */
  hasAllocationRow: boolean;
}

/** One week of the source roster (mirrors backend RosterWeek). */
export interface RosterWeek {
  weekStart: string;
  entries: RosterEntry[];
}

/** The full roster for a source under a scenario (mirrors backend RosterView). */
export interface RosterView {
  source: RosterSource;
  sourceKey: string;
  scenarioKey: string;
  weeks: RosterWeek[];
}

/** Result of a roster PUT / DELETE (mirrors backend RosterMutationResult). */
export interface RosterMutationResult {
  ok: true;
  /** The person's Σ allocation % for the affected week AFTER the write. */
  allocationPercent: number;
  /** D4 warn-only: Σ% > 100. Never a 4xx. */
  weekOverloaded: boolean;
  /** Presence rows materialized (PUT) or soft-deleted (DELETE). */
  presenceRows: number;
  /** True when an allocation row was written/removed. */
  allocationChanged: boolean;
}

// ── GET ─────────────────────────────────────────────────────────────────────

/**
 * Fetch the per-week roster for a plan source under a scenario. For each week
 * the source's tasks span, returns the people present + their allocation %.
 */
export async function getRoster(
  source: RosterSource,
  sourceKey: string,
  scenarioKey?: string,
): Promise<RosterView> {
  const params = new URLSearchParams({ source, sourceKey });
  if (scenarioKey) params.set('scenarioKey', scenarioKey);
  const response = await apiClient.get<RosterView>(`/schedule/roster?${params.toString()}`);
  return response.data;
}

// ── PUT (add / set %) ─────────────────────────────────────────────────────────

export interface SetRosterAllocationPayload {
  source: RosterSource;
  sourceKey: string;
  scenarioKey?: string;
  userKey: string;
  /** BST Saturday 'YYYY-MM-DD'. */
  weekStart: string;
  /** 0–500 (server clamps). Adding a new person → 100 (mentor 25). */
  percent: number;
  /**
   * Default true: also materialize presence rows on every task covering the
   * week (roster "Add person to job" semantics). The task table's single-task
   * assign passes FALSE — it fills the count-once allocation % only, presence
   * stays on the one task it created via bulk-from-task (2026-07-28 fan-out fix).
   */
  materializePresence?: boolean;
}

/**
 * Upsert a person's allocation % for a source-week (materializes their presence
 * on the source's tasks that week). Over-100% NEVER blocks — it comes back in
 * `weekOverloaded` (D4). A day-off collision throws AbsenceDayConflictError.
 */
export async function setRosterAllocation(
  payload: SetRosterAllocationPayload,
): Promise<RosterMutationResult> {
  try {
    const response = await apiClient.put<RosterMutationResult>('/schedule/roster', payload);
    return response.data;
  } catch (err: unknown) {
    const locked = toScenarioLockedError(err);
    if (locked) throw locked;
    const absence = toAbsenceDayConflictError(err);
    if (absence) throw absence;
    throw err;
  }
}

// ── DELETE (remove) ───────────────────────────────────────────────────────────

export interface RemoveRosterAllocationPayload {
  source: RosterSource;
  sourceKey: string;
  scenarioKey?: string;
  userKey: string;
  weekStart: string;
}

/**
 * Remove a person from a source-week roster (clears the allocation row + the
 * materialized presence rows for that scenario). A Submitted plan 409s
 * scenario_locked → ScenarioLockedError.
 */
export async function removeRosterAllocation(
  payload: RemoveRosterAllocationPayload,
): Promise<RosterMutationResult> {
  try {
    // axios DELETE carries the tuple in the request body (the controller accepts
    // it from the body or the query).
    const response = await apiClient.delete<RosterMutationResult>('/schedule/roster', {
      data: payload,
    });
    return response.data;
  } catch (err: unknown) {
    const locked = toScenarioLockedError(err);
    if (locked) throw locked;
    throw err;
  }
}
